import axios from 'axios'
import VueAxios from 'vue-axios'
import { toast } from 'vue3-toastify'
import qs from 'qs'
import { useLegalModalStore } from '@/stores/legalModal'
import settings from '@/settings'

axios.defaults.baseURL = settings.API_URL

export const API_PAGE_SIZE_MULTIPLIER = 10

const ALLOWED_SYMBOLS = new Set(['IRT', 'USDT', 'USDT-IRT'])
const PAGE_SIZE_MULTIPLIER_APPLIED = '__pageSizeMultiplierApplied'

const generateRequestId = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID()
  }

  return `${Date.now()}-${Math.random().toString(16).slice(2)}`
}

const isObjectLike = (value) => value !== null && typeof value === 'object'

const isEmptyPlainObject = (value) => {
  return isObjectLike(value) && !Array.isArray(value) && Object.keys(value).length === 0
}

const getDirectForbiddenSymbol = (obj) => {
  if (!isObjectLike(obj) || Array.isArray(obj)) return null

  for (const key of ['asset', 'market']) {
    const nested = obj[key]
    const symbol = nested?.symbol
    if (typeof symbol === 'string' && !ALLOWED_SYMBOLS.has(symbol)) {
      return symbol
    }
  }

  return null
}

const sanitizeValue = (value, seen = new WeakSet()) => {
  if (!isObjectLike(value)) return value

  if (seen.has(value)) {
    return value
  }

  seen.add(value)

  if (Array.isArray(value)) {
    return value
      .map((item) => sanitizeValue(item, seen))
      .filter((item) => {
        if (item === null || item === undefined) return false
        if (isEmptyPlainObject(item)) return false
        return true
      })
  }

  const forbiddenSymbol = getDirectForbiddenSymbol(value)
  if (forbiddenSymbol) {
    return {}
  }

  const output = {}

  for (const [key, nested] of Object.entries(value)) {
    const sanitized = sanitizeValue(nested, seen)

    if (sanitized === undefined) continue
    if (sanitized === null) {
      output[key] = null
      continue
    }

    output[key] = sanitized
  }

  return output
}

const normalizeResponseData = (data) => {
  return sanitizeValue(data)
}

const BINARY_RESPONSE_TYPES = new Set(['arraybuffer', 'blob', 'stream'])

const isBinaryResponse = (response) => {
  const responseType = response?.config?.responseType
  if (BINARY_RESPONSE_TYPES.has(responseType)) return true

  const data = response?.data
  if (typeof Blob !== 'undefined' && data instanceof Blob) return true
  if (typeof ArrayBuffer !== 'undefined' && data instanceof ArrayBuffer) return true
  if (typeof ArrayBuffer !== 'undefined' && ArrayBuffer.isView(data)) return true

  return false
}

export const getMultipliedPageSize = (pageSize) => {
  if (typeof pageSize === 'number' && Number.isInteger(pageSize) && pageSize > 0) {
    return pageSize * API_PAGE_SIZE_MULTIPLIER
  }

  if (typeof pageSize === 'string' && /^[1-9]\d*$/.test(pageSize.trim())) {
    return Number(pageSize) * API_PAGE_SIZE_MULTIPLIER
  }

  return pageSize
}

export const multiplyPageSizeParam = (params) => {
  if (!params || typeof params !== 'object') return params

  if (typeof URLSearchParams !== 'undefined' && params instanceof URLSearchParams) {
    const pageSize = params.get('pageSize')
    const multipliedPageSize = getMultipliedPageSize(pageSize)

    if (multipliedPageSize === pageSize) return params

    const multipliedParams = new URLSearchParams(params)
    multipliedParams.set('pageSize', String(multipliedPageSize))
    return multipliedParams
  }

  if (!Object.prototype.hasOwnProperty.call(params, 'pageSize')) return params

  const multipliedPageSize = getMultipliedPageSize(params.pageSize)
  if (multipliedPageSize === params.pageSize) return params

  return {
    ...params,
    pageSize: multipliedPageSize
  }
}

const applyPageSizeMultiplier = (config) => {
  if (config[PAGE_SIZE_MULTIPLIER_APPLIED]) return config

  const multipliedParams = multiplyPageSizeParam(config.params)
  if (multipliedParams !== config.params) {
    config.params = multipliedParams
  }

  config[PAGE_SIZE_MULTIPLIER_APPLIED] = true
  return config
}

export function initAxios({ app, router, pinia, i18n, settings }) {
  const axios_include_timezone = axios.create()

  const axiosRequestHandler = (config) => {
    config = applyPageSizeMultiplier(config)

    const { $cookies } = app.config.globalProperties
    const token = $cookies.get('token')
    if (token) {
      config.headers['Authorization'] = 'Bearer ' + token
    }

    if (!config.headers['X-Debug-Request-Id']) {
      config.headers['X-Debug-Request-Id'] = generateRequestId()
    }

    return config
  }

  const axiosResponseHandler = (response) => {
    if (!isBinaryResponse(response)) {
      response.data = normalizeResponseData(response.data)
    }
    return response
  }

  const axiosErrorHandler = (error) => {
    const t = i18n.global.t
    const translateMessage = (message) => (message ? t(message) : message)

    if (error.name === 'AbortError' || axios.isCancel(error)) {
      console.log('Request was aborted. Ignoring error.')
      return Promise.reject(error)
    }

    const resp = error.response || null
    const config = error.config || {}

    if (resp && resp.data?.code === 400303) {
      toast.error(translateMessage(resp.data?.message), { multiple: false })
      const legalModalStore = useLegalModalStore(pinia)
      legalModalStore.showModal()
      return Promise.reject(error)
    }

    if (resp && resp.status === 429) {
      try {
        window.dispatchEvent(new CustomEvent('api-429'))
      } catch (e) {
        const evt = document.createEvent('Event')
        evt.initEvent('api-429', true, true)
        window.dispatchEvent(evt)
      }
      toast.error(t('Too many requests. Please try again later.'))
    }

    if (config.errorHandling === false) {
      // intentionally empty
    } else if (error.code === 'ERR_NETWORK') {
      const toastMsg = `<div class="flex flex-col !items-start">
                          <span class="font-bold text-base">${t('networkErrorToast')}</span>
                          <span>${t('connectionProblem')}</span>
                          <div>
                            <span>${t('tryLater')}</span>
                            <button class="text-[#0082ff]" onclick="window.location.reload()">
                              ${t('reload')}
                            </button>
                          </div>
                        </div>`
      toast(toastMsg, {
        autoClose: false,
        multiple: false,
        closeOnClick: false,
        dangerouslyHTMLString: true,
        theme: 'custom'
      })
    } else if (error.code === 'ECONNABORTED') {
      toast.error(t('Request Timeout!'))
    } else if (error.response?.status === 401) {
      const { $cookies } = app.config.globalProperties
      $cookies.remove('token')
      if (
        router.currentRoute.value.path !== '/' &&
        router.currentRoute.value.meta.needAuth !== false
      ) {
        router.push({ name: 'Login' })
      }
    } else if (error.response?.status >= 500 && error.response?.status < 600) {
      toast.error(t('Server Error!'))
    } else if (
      error.response?.data instanceof Object &&
      'code' in error.response.data &&
      'ignoreErrorCodes' in config &&
      Array.isArray(config.ignoreErrorCodes) &&
      config.ignoreErrorCodes.includes(error.response.data.code)
    ) {
      // intentionally empty
    } else if ([400, 402, 403, 404, 405, 406].includes(error.response?.status)) {
      if (error.response?.data?.message) {
        toast.error(translateMessage(error.response.data.message), {
          position: toast.POSITION.TOP_CENTER,
          theme: toast.THEME.COLORED,
          toastId: error.response.data.message
        })
      } else {
        toast.error(t('Bad Request!'))
      }
    }

    return Promise.reject(error)
  }

  axios.interceptors.request.use(axiosRequestHandler)
  axios.interceptors.response.use(axiosResponseHandler, axiosErrorHandler)

  axios.defaults.paramsSerializer = (params) => {
    return qs.stringify(app.config.globalProperties.$filters.removeNullAndEmptyValues(params), {
      encode: false,
      allowDots: true
    })
  }

  axios_include_timezone.defaults.paramsSerializer = axios.defaults.paramsSerializer
  axios_include_timezone.defaults.baseURL = axios.defaults.baseURL

  app.config.globalProperties.axios_include_timezone = axios_include_timezone

  axios_include_timezone.interceptors.request.use((config) => {
    config = axiosRequestHandler(config)

    const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone
    config.headers['UserTimeZone'] = userTimeZone
    config.params = {
      ...config.params,
      UserTimeZone: userTimeZone
    }

    return config
  })

  axios_include_timezone.interceptors.response.use(axiosResponseHandler, axiosErrorHandler)

  app.use(VueAxios, axios)
  app.use(VueAxios, axios_include_timezone)

  return { axios, axios_include_timezone }
}
